import type { CandidateProfileExperiencesItem } from "openapi/api/model"

/**
 * Careers plotted on a shared time axis, from canvas 27a.
 *
 * The turn's argument is that a list of role strings is what every ATS ships,
 * and that the four roles carry information a list cannot show — "how long each
 * tenure ran, which direction the career moves, where the gaps are" — so they
 * are plotted instead: one track per candidate, every track on the same axis,
 * with the gridlines running down through every row. Bar width is tenure,
 * position is recency.
 *
 * Shared is the load-bearing word. Two candidates' tracks only compare if they
 * are measured against one axis, so the axis is computed across the whole page
 * of results and handed to every row.
 *
 * Pure, and separate from anything that draws: the maths is the part worth
 * testing, and the same numbers feed the desktop row, the drill-down and the
 * phone.
 */

export type Role = {
  /**
   * Stable across the whole model, so a role in a ledger and its bar on a track
   * are the same object to anything that wants to link them — the drill-down
   * lights the segment you are hovering. Assigned once, after sorting, rather
   * than derived at each call site, where two derivations would drift the
   * moment one of them filtered a role out.
   */
  id: string
  title?: string
  company?: string
  description?: string
  /** Whole years. A role without one cannot be placed and is dropped. */
  startYear: number
  /** Absent while it is the role they are in. */
  endYear?: number
  isCurrent: boolean
}

export type Axis = {
  /** The first year on the scale. */
  from: number
  /** The last year on the scale. */
  to: number
  /**
   * Whether that right edge is the present. A career whose last role ended in
   * 2021 does not run to now, and labelling its edge "now" adds five years
   * nobody worked.
   */
  isOpen: boolean
  /** Where the labelled gridlines sit, as percentages from the left. */
  ticks: { year: number; left: number }[]
}

export type Bar = Role & {
  /** Percentages of the axis width. */
  left: number
  width: number
  /**
   * How far back this role is, oldest = 0. The artboard recedes older roles
   * through three tints, so anything past the third stays at the third.
   */
  depth: number
  /** "5y", or "8mo" for a role shorter than a year. */
  tenure: string
  /** "2021–now", "2016–18". */
  range: string
}

/** The longest span the axis will draw, in years. */
const MAX_SPAN = 24
/** Enough of a bar to see and to point at, as a percentage of the axis. */
const MIN_WIDTH = 2.5
/** A role inside one calendar year still gets a share of the phone's track. */
const MIN_TENURE = 0.5
/** Never fewer, so a single short career still reads as a scale. */
const MIN_SPAN = 6

function yearOf(at?: { year?: number | null } | null) {
  const year = at?.year
  return typeof year === "number" && year > 1900 && year < 2200 ? year : undefined
}

/**
 * The roles worth plotting, oldest first.
 *
 * A role with no start year cannot be placed on a scale at all, so it is
 * dropped rather than guessed at — the alternative is a bar that says something
 * untrue about when someone worked somewhere.
 */
export function toRoles(
  experiences: CandidateProfileExperiencesItem[] | undefined,
  now = new Date().getFullYear(),
): Role[] {
  const roles = (experiences ?? [])
    .map((experience) => {
      const startYear = yearOf(experience.starts_at)
      if (startYear === undefined) return undefined
      /*
        One normalisation, and everything downstream reads its result.

        An end year in the future is a typo or a contract's planned end; either
        way the track cannot draw past "now", so it reads as ongoing rather than
        reporting a tenure nobody has served yet. An end before the start is
        simply not a date, and is discarded the same way. `isCurrent` is derived
        from what survives — it used to be read off the raw value, so a role
        ending before it began came out with no end year and `isCurrent: false`,
        and drew as ongoing while labelling itself as ended.
      */
      const rawEnd = yearOf(experience.ends_at)
      const endYear =
        rawEnd !== undefined && rawEnd <= now && rawEnd >= startYear
          ? rawEnd
          : undefined
      const role: Omit<Role, "id"> = {
        title: experience.title ?? undefined,
        company: experience.company ?? undefined,
        description: experience.description ?? undefined,
        startYear,
        endYear,
        isCurrent: endYear === undefined,
      }
      return role
    })
    .filter((role): role is Omit<Role, "id"> => Boolean(role))
    .filter((role) => role.startYear <= now)

  return roles
    .sort((a, b) => a.startYear - b.startYear)
    .map((role, index) => ({
      ...role,
      id: `${role.company ?? "role"}-${role.startYear}-${index}`,
    }))
}

/** A "nice" step that lands 4 or fewer labelled years on the scale. */
function stepFor(span: number) {
  for (const step of [1, 2, 3, 4, 5, 10, 20]) {
    if (Math.ceil(span / step) <= 4) return step
  }
  return 25
}

/**
 * One axis for a whole page of results.
 *
 * It starts at the earliest role anyone on the page holds, so the tracks are
 * comparable, and stops widening at `MAX_SPAN`: one candidate who started in
 * 1994 would otherwise squeeze everybody else's career into the last inch.
 */
export function sharedAxis(
  roleLists: Role[][],
  now = new Date().getFullYear(),
): Axis {
  const starts = roleLists
    .flat()
    .map((role) => role.startYear)
    .filter((year) => year <= now)

  const earliest = starts.length > 0 ? Math.min(...starts) : now - MIN_SPAN
  const span = Math.min(MAX_SPAN, Math.max(MIN_SPAN, now - earliest))
  const from = now - span

  const step = stepFor(span)
  const ticks: { year: number; left: number }[] = []
  for (let year = Math.ceil((from + 1) / step) * step; year < now; year += step) {
    const left = ((year - from) / span) * 100
    // "now" is already labelled hard against the right edge, and a year that
    // lands within a label's width of it prints on top of the word.
    if (left > 88) continue
    ticks.push({ year, left })
  }

  return { from, to: now, isOpen: true, ticks }
}

/** "5y", "8mo" — whole years once there is a whole year to report. */
function tenureLabel(startYear: number, endYear: number) {
  const years = endYear - startYear
  return years >= 1 ? `${years}y` : "<1y"
}

/**
 * "2021–now", "2016–18" — the second year loses its century only when it shares
 * one, so a role running 1999–2001 does not read as "1999–01".
 *
 * Exported because the ledgers write the same range and were formatting it by
 * hand; two spellings of one fact is one of them being wrong.
 */
export function shortRange(role: Role, now = new Date().getFullYear()) {
  if (role.isCurrent) return `${role.startYear}–now`
  const end = role.endYear ?? now
  const sameCentury = Math.floor(end / 100) === Math.floor(role.startYear / 100)
  return `${role.startYear}–${sameCentury ? String(end).slice(2) : end}`
}

/** "2018–2021" — both years in full, which is what the drill-down ledger draws. */
export function fullRange(role: Role, now = new Date().getFullYear()) {
  return role.isCurrent
    ? `${role.startYear}–now`
    : `${role.startYear}–${role.endYear ?? now}`
}

/**
 * The bars for one candidate, positioned on the shared axis.
 *
 * A role that began before the axis does is clipped to the left edge rather
 * than dropped: the tenure it shows is then shorter than the tenure it ran, but
 * a career that runs off the scale is the truthful reading of a bar that starts
 * at the edge, and dropping it would lose the role entirely.
 */
export function barsFor(roles: Role[], axis: Axis): Bar[] {
  const span = axis.to - axis.from
  if (span <= 0) return []

  const placed = roles.filter((role) => (role.endYear ?? axis.to) > axis.from)

  return placed.map((role, index) => {
    const start = Math.max(role.startYear, axis.from)
    const end = Math.min(role.endYear ?? axis.to, axis.to)
    /*
      A role inside one calendar year would otherwise be a zero-width bar, and a
      role that began this year sits at 100% with nothing to the right to grow
      into — so the floor comes out of the left edge instead of the right, and
      the bar stays visible however recent the role.
    */
    const width = Math.max(((end - start) / span) * 100, MIN_WIDTH)
    const left = Math.max(0, Math.min(((start - axis.from) / span) * 100, 100 - width))

    return {
      ...role,
      left,
      width: Math.min(width, 100 - left),
      depth: index,
      tenure: tenureLabel(role.startYear, role.endYear ?? axis.to),
      range: shortRange(role, axis.to),
    }
  })
}

/**
 * The same roles as one unbroken track, for the phone — canvas 30d.
 *
 * "Twelve years as one unbroken track cut into four segments by tenure."
 * Unbroken is the word: this is a *proportional* track, not a positional one.
 * Each segment takes the share of the width its tenure is of the career, and
 * they run end to end — so the labelled years are the joins between roles
 * rather than a uniform scale, which is exactly what 30d draws.
 *
 * That makes it a different reading from the shared axis on 27a, deliberately.
 * The shared axis exists to compare one candidate against the others on the
 * page; on a screen showing one person there is nothing to compare against, and
 * the question becomes how their own time was divided. It also means the
 * arithmetic cannot overflow: the widths are shares of one whole.
 */
export function continuousSegments(roles: Role[], now = new Date().getFullYear()) {
  if (roles.length === 0)
    return { segments: [], from: now, to: now, isOpen: true, ticks: [] }

  const from = roles[0].startYear
  /*
    The end of *their* career, not the end of the calendar. Forcing this to
    `now` stretched an ended career across years nobody worked and labelled the
    right edge "now" while the last role finished in 2021.
  */
  const to = Math.max(...roles.map((role) => role.endYear ?? now))
  const isOpen = roles.some((role) => role.isCurrent)

  const tenures = roles.map((role) =>
    Math.max(MIN_TENURE, (role.endYear ?? to) - role.startYear),
  )
  const served = tenures.reduce((sum, years) => sum + years, 0)

  let cursor = 0
  const segments = roles.map((role, index) => {
    const width = (tenures[index] / served) * 100
    const left = cursor
    cursor += width
    return {
      ...role,
      left,
      // The last one closes on 100 exactly, whatever the rounding did.
      width: index === roles.length - 1 ? Math.max(0, 100 - left) : width,
      depth: index,
      tenure: tenureLabel(role.startYear, role.endYear ?? to),
      range: shortRange(role, to),
    }
  })

  /*
    The joins, not a scale. A boundary too close to the one before it would
    print on top of it, so it is dropped rather than overlapped — the segment is
    still there to point at.
  */
  const ticks: { year: number; left: number }[] = []
  for (const segment of segments) {
    const last = ticks[ticks.length - 1]
    if (last && segment.left - last.left < 12) continue
    if (segment.left > 88) continue
    ticks.push({ year: segment.startYear, left: segment.left })
  }

  return { segments, from, to, isOpen, ticks }
}

/**
 * The role to lead with: the one they are in, or the last one they held.
 *
 * Not simply the latest by start year. Histories overlap — a side role begun
 * after the main one, a contract that ran alongside — and picking by start date
 * can name an ended role as the headline while a different bar carries the teal
 * "current" edge, so the sentence and the picture disagree.
 */
export function leadRole(roles: Role[]): Role | undefined {
  if (roles.length === 0) return undefined
  const current = roles.filter((role) => role.isCurrent)
  if (current.length > 0) return current[current.length - 1]
  return roles[roles.length - 1]
}

/** "12 years · 4 roles", the line 30d puts above the track. */
export function careerSummary(roles: Role[], now = new Date().getFullYear()) {
  if (roles.length === 0) return undefined
  const from = roles[0].startYear
  const to = Math.max(...roles.map((role) => role.endYear ?? now))
  const years = Math.max(1, to - from)
  return `${years} years · ${roles.length} role${roles.length === 1 ? "" : "s"}`
}
